import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, input, output } from '@angular/core';
import type { WaHideEvent } from '@web.awesome.me/webawesome-pro';

/** The image a lightbox displays: a presigned/display URL plus its caption. */
export type LightboxImage = {
    url: string;
    caption: string | null;
};

/** Fullscreen image viewer: a viewport-filling wa-dialog showing one image with
 *  its caption underneath. Signal-driven like every dialog in the app — the
 *  parent owns which image is open (`image` null = closed) and clears it on
 *  `closed`. With `editable` (admin editor), the caption renders as a textarea
 *  and every keystroke emits `captionChange`; persistence stays with the parent.
 *  Closes on Escape, the close button, or a click on the empty backdrop area,
 *  and is sized with dvh/contain so it works on mobile viewports. */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-image-lightbox',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    styleUrl: './image-lightbox.css',
    templateUrl: './image-lightbox.html',
})
export class ImageLightbox {
    readonly image = input<LightboxImage | null>(null);
    readonly editable = input(false);

    readonly closed = output<void>();
    readonly captionChange = output<string>();

    onHide(e: WaHideEvent) {
        // Ignore wa-hide bubbling from nested elements inside the dialog.
        if ((e.target as HTMLElement | null)?.tagName?.toLowerCase() !== 'wa-dialog') {
            return;
        }
        this.closed.emit();
    }

    close() {
        this.closed.emit();
    }

    /** Standard lightbox affordance: clicking the empty area around the image
     *  (not the image, caption, or controls) closes it. */
    onBackdropClick(e: MouseEvent) {
        if (e.target === e.currentTarget) {
            this.closed.emit();
        }
    }

    onCaptionInput(e: Event) {
        this.captionChange.emit((e.target as HTMLTextAreaElement).value);
    }
}
